import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  firstLink,
  parseInternalPath,
  type InternalLinkRef,
  type InternalUnfurl,
} from '@itsappy/shared';
import { useInternalUnfurl, useLinkPreview } from '@/hooks/queries';

/** Recognises links back into this workspace so they render as real cards. */
function parseInternalLink(url: string): InternalLinkRef | null {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parseInternalPath(parsed.pathname + parsed.search);
  } catch {
    return null;
  }
}

const KIND_LABELS: Record<InternalLinkRef['kind'], string> = {
  chat: 'Conversation',
  task: 'Task',
  project: 'Project',
  space: 'Space',
  whiteboard: 'Whiteboard',
  meeting: 'Meeting',
};

/** `in_progress` → `In progress`. */
function statusLabel(status: string): string {
  const words = status.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** The second line under the title, from whichever fields the kind carries. */
function subtitle(card: InternalUnfurl): string {
  const parts: string[] = [];
  if (card.status) parts.push(statusLabel(card.status));
  if (card.dueDate) parts.push(`Due ${shortDate(card.dueDate)}`);
  if (card.startsAt && !card.status) {
    const start = new Date(card.startsAt);
    parts.push(
      `${shortDate(card.startsAt)}, ${start.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`,
    );
  }
  if (card.detail) parts.push(card.detail);
  return parts.join(' · ');
}

/**
 * A workspace link as a live card: title and status fetched with the viewer's
 * own permissions. While the card loads — or when the object is out of this
 * viewer's reach — the link falls back to the plain "open here" chip, which
 * is also all a private object ever admits to.
 */
function InternalCard({ link }: { link: InternalLinkRef }) {
  const { data: card } = useInternalUnfurl(link.path);

  if (!card) {
    return (
      <Link
        to={link.path}
        className="mt-1.5 flex w-fit max-w-md items-center gap-2 rounded-lg border-l-2 border-brand-500 bg-surface-2 px-2.5 py-1.5 text-xs transition-colors hover:bg-surface-3"
      >
        <span className="text-slate-500">{KIND_LABELS[link.kind]}</span>
        <span className="ink-link truncate">Open in Itsappy Lab</span>
      </Link>
    );
  }

  const sub = subtitle(card);
  return (
    <Link
      to={card.path}
      className="mt-1.5 block w-fit max-w-md rounded-lg border-l-2 border-brand-500 bg-surface-2 px-2.5 py-1.5 transition-colors hover:bg-surface-3"
    >
      <span className="block text-[10px] uppercase tracking-wide text-slate-500">
        {KIND_LABELS[card.kind]}
      </span>
      <span className="block truncate text-xs font-medium text-slate-200">{card.title}</span>
      {sub && <span className="block truncate text-[11px] text-slate-400">{sub}</span>}
    </Link>
  );
}

/**
 * The card under a message that contains a link.
 *
 * Only the first link is unfurled: a message with six links should not fire
 * six requests and grow six cards. Internal links resolve against our own
 * API instead of the Open Graph scraper.
 */
export function LinkUnfurl({ body }: { body: string }) {
  const url = useMemo(() => firstLink(body), [body]);
  const internal = useMemo(() => (url ? parseInternalLink(url) : null), [url]);
  // Internal links resolve via their own endpoint, so the scrape is skipped.
  const { data } = useLinkPreview(internal ? undefined : url);

  if (!url) return null;

  if (internal) return <InternalCard link={internal} />;

  if (!data || data.failed || (!data.title && !data.description)) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="mt-1.5 flex w-fit max-w-md gap-2.5 overflow-hidden rounded-lg border-l-2 border-surface-4 bg-surface-2 p-2 transition-colors hover:bg-surface-3"
    >
      {data.imageUrl && (
        <img
          src={data.imageUrl}
          alt=""
          loading="lazy"
          className="h-14 w-14 shrink-0 rounded object-cover"
          // A broken preview image should leave the card, not a torn icon.
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      )}
      <span className="min-w-0">
        {data.siteName && (
          <span className="block truncate text-[10px] uppercase tracking-wide text-slate-500">
            {data.siteName}
          </span>
        )}
        {data.title && (
          <span className="block truncate text-xs font-medium text-slate-200">{data.title}</span>
        )}
        {data.description && (
          <span className="line-clamp-2 block text-[11px] leading-snug text-slate-400">
            {data.description}
          </span>
        )}
      </span>
    </a>
  );
}
